import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoSearchOutline,
  IoChevronDown,
  IoClose,
  IoRocketOutline,
  IoPersonOutline,
  IoCardOutline,
  IoBagHandleOutline,
  IoPlayCircleOutline,
  IoTvOutline,
  IoHelpCircleOutline,
} from "react-icons/io5";
import HttpApis from "../../services/HttpApis";
import Footer from "../Footer/Footer";
import { HELP_SECTIONS } from "./helpFaqData";
import "./HelpCenter.css";

const SECTION_ICONS = {
  start: <IoRocketOutline />,
  account: <IoPersonOutline />,
  billing: <IoCardOutline />,
  purchase: <IoBagHandleOutline />,
  play: <IoPlayCircleOutline />,
  device: <IoTvOutline />,
};

const normalize = (s) => String(s || "").toLowerCase();

// Cards are dealt into fixed columns here (not CSS columns/grid) so opening a
// question only grows its own card: CSS columns rebalance and move cards
// between columns, and grid rows stretch the card beside it.
const COLUMN_MIN_WIDTH = 420;
const COLUMN_GAP = 16;

const useColumnCount = (ref) => {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () =>
      setCount(Math.max(1, Math.floor((el.clientWidth + COLUMN_GAP) / (COLUMN_MIN_WIDTH + COLUMN_GAP))));
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return count;
};

// Shortest-column-first by question count, so columns come out roughly even.
const distribute = (sections, columns) => {
  const cols = Array.from({ length: columns }, () => ({ items: [], weight: 0 }));
  sections.forEach((s) => {
    const target = cols.reduce((min, c) => (c.weight < min.weight ? c : min), cols[0]);
    target.items.push(s);
    target.weight += s.items.length + 1.5;
  });
  return cols.map((c) => c.items);
};

export const Answer = ({ parts }) =>
  parts.map((p, i) =>
    Array.isArray(p) ? (
      <ul key={i}>
        {p.map((li, j) => (
          <li key={j}>{li}</li>
        ))}
      </ul>
    ) : (
      <p key={i}>{p}</p>
    )
  );

/**
 * Viewer Help Center (FAQ). Static content from helpFaqData.js; the
 * Buying & renting section only shows where buy/rent exists, which the backend
 * signals by offering the "purchase" support category.
 */
const HelpCenter = () => {
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [hasPurchase, setHasPurchase] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    HttpApis.get("client/v1/support/options")
      .then((res) => {
        const list = res?.data?.data?.categories;
        if (alive && Array.isArray(list)) setHasPurchase(list.some((c) => c.key === "purchase"));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const sections = useMemo(
    () => HELP_SECTIONS.filter((s) => !s.requires || (s.requires === "purchase" && hasPurchase)),
    [hasPurchase]
  );

  const q = normalize(query.trim());
  const visible = useMemo(
    () =>
      sections
        .filter((s) => section === "all" || s.id === section)
        .map((s) => ({
          ...s,
          items: q ? s.items.filter((i) => normalize(`${i.q} ${i.a.flat().join(" ")}`).includes(q)) : s.items,
        }))
        .filter((s) => s.items.length > 0),
    [sections, section, q]
  );
  const resultCount = visible.reduce((n, s) => n + s.items.length, 0);

  const gridRef = useRef(null);
  const columnCount = useColumnCount(gridRef);
  const columns = useMemo(
    () => distribute(visible, Math.min(columnCount, Math.max(1, visible.length))),
    [visible, columnCount]
  );

  return (
    <div className="hc-page">
      <div className="hc-logo-container">
        <Link to="/home">
          <img
            src={`${process.env.PUBLIC_URL}/${process.env.REACT_APP_APP_LOGO}`}
            alt="Logo"
            className="hc-logo"
          />
        </Link>
      </div>

      <main className="hc-main">
        <header className="hc-hero">
          <p className="hc-eyebrow">Help Center</p>
          <h1 className="hc-title">How can we help?</h1>
          <p className="hc-subtitle">
            Answers about your account, subscription and watching. Can't find it?{" "}
            <Link to="/support" className="hc-inline-link">
              Contact Support
            </Link>
            .
          </p>

          <div className="hc-search-wrap">
            <IoSearchOutline className="hc-search-icon" />
            <input
              type="search"
              className="hc-search"
              placeholder="Search, e.g. “cancel subscription” or “activate TV”"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpenId(null);
              }}
              aria-label="Search the Help Center"
            />
            {query && (
              <button type="button" className="hc-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
                <IoClose />
              </button>
            )}
          </div>
        </header>

        <div className="hc-chips" role="tablist" aria-label="Help topics">
          {[{ id: "all", title: "All topics" }, ...sections].map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={section === s.id}
              className={`hc-chip ${section === s.id ? "hc-chip--active" : ""}`}
              onClick={() => {
                setSection(s.id);
                setOpenId(null);
              }}
            >
              {s.title}
            </button>
          ))}
        </div>

        {resultCount === 0 && (
          <div className="hc-empty">
            <p>No answers match “{query.trim()}”.</p>
            <p>
              Didn't find what you need?{" "}
              <Link to="/support" className="hc-inline-link">
                Contact Support
              </Link>{" "}
              and we'll help you out.
            </p>
            <button type="button" className="hc-btn hc-btn--outline" onClick={() => setQuery("")}>
              Clear search
            </button>
          </div>
        )}

        <div ref={gridRef} className="hc-grid" style={{ display: resultCount === 0 ? "none" : undefined }}>
          {columns.map((col, ci) => (
            <div key={ci} className="hc-column">
              {col.map((s) => (
                <section key={s.id} className="hc-card">
                  <header className="hc-card-head">
                    <span className="hc-card-icon">{SECTION_ICONS[s.icon] || <IoHelpCircleOutline />}</span>
                    <h2 className="hc-card-title">{s.title}</h2>
                    <span className="hc-card-count">{s.items.length}</span>
                  </header>
                  <ul className="hc-list">
                    {s.items.map((item, idx) => {
                      const id = `${s.id}-${idx}`;
                      const open = openId === id || (q && resultCount <= 3);
                      return (
                        <li key={id} className={`hc-item ${open ? "hc-item--open" : ""}`}>
                          <button
                            type="button"
                            className="hc-question"
                            onClick={() => setOpenId(openId === id ? null : id)}
                            aria-expanded={open}
                            aria-controls={`hc-${id}`}
                          >
                            <span>{item.q}</span>
                            <IoChevronDown className="hc-chev" />
                          </button>
                          {open && (
                            <div id={`hc-${id}`} className="hc-answer">
                              <Answer parts={item.a} />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          ))}
        </div>

        <div className="hc-cta">
          <div>
            <h2 className="hc-cta-title">Still need help?</h2>
            <p className="hc-cta-text">Send us a message with a screenshot and we'll reply by email.</p>
          </div>
          <Link to="/support" className="hc-btn">
            Contact Support
          </Link>
        </div>
      </main>

      <Footer />
      <div className="hc-footer-copy">© {new Date().getFullYear()} All Rights Reserved.</div>
    </div>
  );
};

export default HelpCenter;
